import type { CompanyAuthSession } from '@crv/contracts/auth';
import {
  CASH_PERMISSION_KEYS,
  PURCHASES_SETTINGS_MANAGE_PERMISSION,
  SHIPMENTS_WEIGH_EVIDENCE_CAPTURE_PERMISSION,
  TARE_PROFILES_MANAGE_PERMISSION,
  TARE_PROFILES_VIEW_PERMISSION,
  UNIT_PROFILES_MANAGE_PERMISSION,
  UNIT_PROFILES_VIEW_PERMISSION,
  type PermissionKey,
} from '@crv/contracts/permissions';

import { hasFeature, hasPermission } from './authorization';

/**
 * Web module access is policy-driven:
 * - Navigation shows only modules that pass this policy.
 * - Most routes use the same policy to deny access early.
 * - Quick Capture navigation is broadened by an assigned-task availability signal in MainLayout.
 * - Quick Capture is directly routable so users without broad shipment permissions can let the API
 *   resolve assigned tasks or return an empty state.
 * - Pages still perform fine-grained action checks as a deep-link fallback.
 */
export type ModuleKey =
  | 'company'
  | 'companyPurchaseSettlement'
  | 'companyPurchaseFolioSequence'
  | 'companyPurchaseAdjustments'
  | 'stores'
  | 'storeLocations'
  | 'locationStations'
  | 'locationYardLocations'
  | 'locationUnitProfiles'
  | 'locationInventoryPolicies'
  | 'team'
  | 'invites'
  | 'companyRoles'
  | 'employees'
  | 'holdingUnits'
  | 'shipments'
  | 'shipmentQuickCapture'
  | 'companyReceivers'
  | 'purchases'
  | 'cash'
  | 'inventoryReconciliation'
  | 'offline'
  | 'materialsCatalog'
  | 'customers'
  | 'timeTracking'
  | 'timeTrackingSelf';

export interface ModuleAccessRule {
  requiredFeatures?: readonly string[];
  anyPermissions?: readonly PermissionKey[];
}

const TIME_TRACKING_ENTRY_PERMISSIONS: readonly PermissionKey[] = [
  'timeTracking.entry.view.self',
  'timeTracking.entry.view.team',
  'timeTracking.entry.create',
  'timeTracking.entry.close',
  'timeTracking.entry.approve',
  'timeTracking.policy.manage',
  'timeTracking.period.close',
  'employees.manage',
];

const TIME_TRACKING_SELF_ENTRY_PERMISSIONS: readonly PermissionKey[] = [
  'timeTracking.entry.create',
  'timeTracking.entry.close',
];

const PURCHASE_ADJUSTMENTS_MANAGE_PERMISSION: PermissionKey = 'purchases.adjustments.manage';

const UNIT_TARE_PROFILE_READ_PERMISSIONS: readonly PermissionKey[] = [
  UNIT_PROFILES_VIEW_PERMISSION,
  UNIT_PROFILES_MANAGE_PERMISSION,
  TARE_PROFILES_VIEW_PERMISSION,
  TARE_PROFILES_MANAGE_PERMISSION,
  'locations.manage',
  'holdingUnits.view',
  'holdingUnits.manage',
];

export const WEB_MODULE_ACCESS: Record<ModuleKey, ModuleAccessRule> = {
  company: {
    anyPermissions: [
      'tenancy.company.manage',
      PURCHASES_SETTINGS_MANAGE_PERMISSION,
      PURCHASE_ADJUSTMENTS_MANAGE_PERMISSION,
    ],
  },
  companyPurchaseSettlement: {
    requiredFeatures: ['purchases.crv'],
    anyPermissions: [PURCHASES_SETTINGS_MANAGE_PERMISSION],
  },
  companyPurchaseFolioSequence: {
    requiredFeatures: ['purchases.crv'],
    anyPermissions: [PURCHASES_SETTINGS_MANAGE_PERMISSION],
  },
  companyPurchaseAdjustments: {
    requiredFeatures: ['purchases.crv'],
    anyPermissions: [PURCHASE_ADJUSTMENTS_MANAGE_PERMISSION],
  },
  stores: {
    anyPermissions: ['stores.manage'],
  },
  storeLocations: {
    anyPermissions: ['locations.manage'],
  },
  locationStations: {
    anyPermissions: ['stations.manage'],
  },
  locationYardLocations: {
    anyPermissions: ['yardLocations.view', 'yardLocations.manage', 'locations.manage'],
  },
  locationUnitProfiles: {
    anyPermissions: UNIT_TARE_PROFILE_READ_PERMISSIONS,
  },
  locationInventoryPolicies: {
    anyPermissions: ['holdingUnits.manage'],
  },
  team: {
    // Team list/change endpoints are currently guarded by tenancy.company.manage.
    anyPermissions: ['tenancy.company.manage'],
  },
  invites: {
    // Invite endpoints are currently guarded by tenancy.company.manage.
    anyPermissions: ['tenancy.company.manage'],
  },
  companyRoles: {
    anyPermissions: ['roles.view'],
  },
  employees: {
    requiredFeatures: ['employees.basic'],
    anyPermissions: ['employees.view', 'employees.manage'],
  },
  holdingUnits: {
    requiredFeatures: ['holdingUnits.basic'],
    anyPermissions: ['holdingUnits.view', 'holdingUnits.manage'],
  },
  shipments: {
    requiredFeatures: ['shipments.basic'],
    anyPermissions: [
      'shipments.view',
      'shipments.create',
      'shipments.update',
      'shipments.export',
      'coverShipments.view',
      'coverShipments.create',
    ],
  },
  shipmentQuickCapture: {
    requiredFeatures: ['shipments.basic'],
    anyPermissions: [SHIPMENTS_WEIGH_EVIDENCE_CAPTURE_PERMISSION],
  },
  companyReceivers: {
    requiredFeatures: ['shipments.basic'],
    anyPermissions: ['companyReceivers.view'],
  },
  purchases: {
    requiredFeatures: ['purchases.crv'],
    anyPermissions: ['purchases.view', 'purchases.create'],
  },
  cash: {
    requiredFeatures: ['cash.basic'],
    anyPermissions: CASH_PERMISSION_KEYS,
  },
  inventoryReconciliation: {
    requiredFeatures: ['purchases.crv', 'holdingUnits.basic'],
    anyPermissions: ['purchases.view', 'holdingUnits.view', 'holdingUnits.manage'],
  },
  offline: {
    anyPermissions: ['importPacks.issue'],
  },
  materialsCatalog: {
    requiredFeatures: ['materialsCatalog.basic'],
    anyPermissions: ['materialsCatalog.view'],
  },
  customers: {
    requiredFeatures: ['customers.basic'],
    anyPermissions: [
      'customers.view',
      'customers.create',
      'customers.update',
      'customers.deactivate',
      'customers.identifier.manage',
    ],
  },
  timeTracking: {
    requiredFeatures: ['timeTracking.basic'],
    anyPermissions: TIME_TRACKING_ENTRY_PERMISSIONS,
  },
  timeTrackingSelf: {
    requiredFeatures: ['timeTracking.basic'],
    anyPermissions: TIME_TRACKING_SELF_ENTRY_PERMISSIONS,
  },
};

export function canAccessModule(
  currentUser: CompanyAuthSession | null | undefined,
  rule: ModuleAccessRule,
): boolean {
  const featureAllowed = (rule.requiredFeatures ?? []).every((feature) =>
    hasFeature(currentUser, feature),
  );
  if (!featureAllowed) {
    return false;
  }

  const entryPermissions = rule.anyPermissions ?? [];
  if (entryPermissions.length === 0) {
    return true;
  }

  return entryPermissions.some((permission) => hasPermission(currentUser, permission));
}

export function canAccessWebModule(
  currentUser: CompanyAuthSession | null | undefined,
  module: ModuleKey,
): boolean {
  return canAccessModule(currentUser, WEB_MODULE_ACCESS[module]);
}

export function canAccessQuickCaptureNavigation(
  currentUser: CompanyAuthSession | null | undefined,
  hasActiveAssignedCaptureTargets: boolean,
): boolean {
  return (
    canAccessWebModule(currentUser, 'shipmentQuickCapture') ||
    (hasFeature(currentUser, 'shipments.basic') && hasActiveAssignedCaptureTargets)
  );
}
